<template>
  <div
    class="chat-message"
    :class="{
      'chat-message--user': message.role === 'user',
      'chat-message--assistant': message.role === 'assistant',
    }"
    :data-message-id="message.id"
  >
    <!--
      Assistant identity mark (DS AtlasChat treatment): compass + "Atlas"
      name label above the turn, body indented underneath — replies read
      as authored prose rather than a chat bubble.
    -->
    <div v-if="message.role === 'assistant'" class="atlas-turn-mark">
      <AtlasLogo :size="18" variant="brand" />
      <span class="atlas-turn-mark__name">Atlas</span>
    </div>

    <div class="bubble-wrapper">
      <!-- Tool pills -->
      <div
        v-if="message.toolCalls && message.toolCalls.length > 0"
        class="tool-pills"
      >
        <ToolCallIndicator
          v-for="(call, i) in message.toolCalls"
          :key="i"
          :call="call"
        />
      </div>

      <!--
        Thinking trail: reasoning monologue captured across tool-use
        iterations. Shown above the final answer as a collapsible
        disclosure — open by default so the user can see what Atlas
        was working through, collapsible for scan-readability later.
      -->
      <details
        v-if="message.thinkingTrail"
        class="thinking-trail"
      >
        <summary class="thinking-trail__summary">
          <v-icon size="12" color="grey darken-1">mdi-brain</v-icon>
          Reasoning
        </summary>
        <div class="thinking-trail__body">
          <MarkdownContent
            :text="message.thinkingTrail"
            :document-index="documentIndex"
            @ref-click="onRefClick"
          />
        </div>
      </details>

      <!-- Message bubble wrapper (hover target for copy button) -->
      <div class="bubble-hover-wrap">
        <!--
          Pending narration bubble. Two sub-states:
          (a) No text yet → spinner + "Rendering…" label.
          (b) Text streaming in → render partial markdown live with
              a trailing cursor. Much more engaging than a spinner
              holding for 20s while the full output composes.
        -->
        <div v-if="message.pendingNarration && !message.text" class="pending-narration">
          <span class="pending-narration-spinner" aria-hidden="true" />
          <span class="pending-narration-text">
            Rendering {{ message.chainName ? message.chainName + ' ' : '' }}output…
          </span>
        </div>
        <div
          v-else-if="message.pendingNarration && message.text"
          class="bubble bubble--assistant"
        >
          <MarkdownContent
            :text="cleanStreamingText(message.text)"
            :document-index="documentIndex"
            @ref-click="onRefClick"
          />
          <span class="streaming-cursor">▍</span>
        </div>
        <!-- Card-only assistant messages (workflow auto-start: text
             suppressed, the ui_block below is the whole response) render
             no bubble at all — an empty pill or "(no text response)"
             would sit above the card for no reason. -->
        <div
          v-else-if="message.text || !(message.uiBlocks && message.uiBlocks.length)"
          class="bubble"
          :class="message.role === 'user' ? 'bubble--user' : 'bubble--assistant'"
        >
          <MarkdownContent
            v-if="message.role === 'assistant' && message.text"
            :text="message.text"
            :document-index="documentIndex"
            @ref-click="onRefClick"
          />
          <span v-else-if="message.role === 'user' && message.text" class="user-text">
            {{ displayUserText }}
          </span>
          <span
            v-else-if="message.role === 'assistant'"
            class="no-response"
          >(no text response)</span>
        </div>

        <!--
          Hover-revealed action row for assistant messages. Copy is always
          present; Retry only appears on the LATEST assistant message (via
          `canRetry` from the page), since retrying an older turn would
          discard everything that came after and confuse the thread.
        -->
        <div
          v-if="message.role === 'assistant' && message.text && !message.pendingNarration"
          class="msg-actions"
        >
          <button
            v-if="canRetry"
            type="button"
            class="msg-action-btn"
            title="Retry — regenerate this response"
            @click="$emit('retry')"
          >
            <v-icon size="12" color="grey darken-1">mdi-refresh</v-icon>
            <span>Retry</span>
          </button>
          <button
            type="button"
            class="msg-action-btn"
            :title="copied ? 'Copied!' : 'Copy message'"
            @click="copyToClipboard"
          >
            <v-icon size="12" color="grey darken-1">
              {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
            </v-icon>
            <span>{{ copied ? 'Copied' : 'Copy' }}</span>
          </button>
        </div>
      </div>

      <!--
        Workflow ui_blocks (assistant messages only). Engine-authored
        interactive components (e.g. a choice step) lifted from workflow
        tool results. Interactive only while this is the latest assistant
        message — superseded steps render inert so scroll-back stays
        coherent. Acting on one composes a [workflow:action] message and
        rides the existing follow-up channel to sendMessage.
      -->
      <div
        v-if="message.role === 'assistant' && uiBlocksToRender.length > 0"
        class="ui-blocks"
      >
        <UiBlockRenderer
          v-for="(block, blockIndex) in uiBlocksToRender"
          :key="block.block_id"
          :block="block"
          :interactive="message.isLatestAssistant === true && !message.pendingNarration && isBlockCurrent(block, blockIndex)"
          :parked-eligible="message.isLatestUiBlocks === true && message.isLatestAssistant !== true"
          :parked-instance-id="parkedInstanceId"
          :summary-available="canShowDocumentSummaryHandler"
          :open-available="canOpenDocumentHandler"
          :compare-available="canCompareDocumentsHandler"
          :exit-workflow-key="exitWorkflowKey"
          @action="onUiBlockAction"
          @open-summary="(event, doc) => $emit('open-summary', event, doc)"
          @open-document="(doc) => $emit('open-document', doc)"
          @compare-documents="(docs) => $emit('compare-documents', docs)"
        />
      </div>

      <!--
        Suggested follow-up chips (assistant messages only). Rendered below
        the bubble; click sends the suggestion as the next user message.
        Only the most recent turn keeps its chips — the composable clears
        stale ones when a new question is sent.
      -->
      <div
        v-if="message.role === 'assistant' && displayFollowUps.length > 0"
        class="follow-up-chips"
      >
        <button
          v-for="(question, i) in displayFollowUps"
          :key="i"
          type="button"
          class="follow-up-chip"
          @click="$emit('follow-up', question)"
        >
          <v-icon size="12" color="grey">mdi-arrow-right</v-icon>
          <span>{{ question }}</span>
        </button>
      </div>

      <!--
        Document chips + compare CTA. The flyout dispatches these as
        page actions ('document.open', 'document.compare'); each chip
        only renders when the current route has a handler registered,
        so a button is never visible unless clicking it will actually
        do something. Pages opt in via `usePageAction(...)`.
      -->
      <div
        v-if="canShowDocumentSummary || canCompareDocuments || canCompareLevels"
        class="follow-up-chips"
      >
        <!-- <template v-if="canShowDocumentSummary">
          <button
            v-for="(doc, i) in documents"
            :key="'d' + i"
            type="button"
            class="document-chip"
            @click="$emit('open-summary', $event, doc)"
            :title="doc.reference + ' - click to preview document'"
          >
            <v-icon size="12" color="grey">mdi-arrow-right</v-icon>
            <span>{{ doc.doc_name }}</span>
          </button>
        </template> -->
        <button
          v-if="canCompareDocuments"
          type="button"
          class="compare-cta"
          :title="`Compare ${documents.length} documents side-by-side`"
          @click="$emit('compare-documents', documents)"
        >
          <v-icon size="14" color="white">mdi-compare</v-icon>
          <span>Compare {{ documents.length }} documents</span>
        </button>
        <button
          v-if="canCompareLevels"
          type="button"
          class="compare-cta"
          :title="`Compare levelling across ${evaluatedDocuments.length} evaluated jobs`"
          @click="$emit('compare-levels', evaluatedDocuments)"
        >
          <v-icon size="14" color="white">mdi-scale-balance</v-icon>
          <span>Level Comparison ({{ evaluatedDocuments.length }} docs)</span>
        </button>
      </div>

      <!--
        Re-open review panel CTA. Shown on assistant messages that
        dispatched a `document.suggest-content` page action — once the
        user closes the review wizard (skip-all, X, or finishing every
        suggestion) the panel state is cleared, so this button is the
        only way back into it. Click re-dispatches the same payload,
        which re-runs `beginSuggestionReview` in the host page and
        re-mounts the wizard with the original suggestions.

        Visible whenever the host page has a handler registered, even
        if the wizard is currently open — when it's open, the chat is
        hidden behind it so the user can't actually see this button.
      -->
      <div v-if="canReopenReview" class="follow-up-chips">
        <button
          type="button"
          class="review-reopen-cta"
          title="Re-open the suggestion review panel"
          @click="onReopenReview"
        >
          <v-icon size="14" color="white">mdi-clipboard-edit-outline</v-icon>
          <span>Open review panel</span>
        </button>
      </div>

      <!--
        Document Creation Agent CTA — rendered as a full agent card
        (same Atlas compass, same emerald palette, same status-row
        layout as the right-hand panel card) so users see it as
        another governed agent stepping in with the next official
        action. Placed below the follow-up chips so it reads as the
        committed next step rather than just another suggestion.
      -->
      <div v-if="canCreateDocument" class="doc-cta-card">
        <div class="doc-cta-card__row">
          <div
            class="doc-cta-card__avatar"
            :class="{ 'doc-cta-card__avatar--pulsing': docCreationSubmitting }"
          >
            <AtlasLogo :size="22" variant="brand" />
          </div>
          <div class="doc-cta-card__meta">
            <div class="doc-cta-card__name">Document Creation Agent</div>
            <div class="doc-cta-card__status-row">
              <span
                class="doc-cta-card__dot"
                :class="{ 'doc-cta-card__dot--pulse': docCreationSubmitting || docCreationStarted }"
              />
              <span class="doc-cta-card__status">{{ ctaStatusLabel }}</span>
            </div>
          </div>
          <button
            v-if="!docCreationStarted"
            type="button"
            class="doc-cta-card__btn"
            :disabled="docCreationSubmitting"
            @click="onCreateDocument"
          >
            <v-icon
              size="14"
              color="white"
              :class="{ 'doc-cta-card__spin': docCreationSubmitting }"
            >
              {{ docCreationSubmitting ? 'mdi-autorenew' : 'mdi-play' }}
            </v-icon>
            {{ docCreationSubmitting ? 'Starting…' : 'Run' }}
          </button>
          <span v-else class="doc-cta-card__started">
            <v-icon size="12" color="green darken-2">mdi-check</v-icon>
            Running
          </span>
        </div>
        <p class="doc-cta-card__subtitle">
          Create this document from the generated output via RoleMapper's
          governed creation pipeline — the agent will build it and post
          the reference here when it's ready.
        </p>
      </div>

      <!--
        Attachment chips (user messages only). Sit under the bubble so they
        read as "I attached these" rather than part of the text. Click →
        open the viewer dialog with the extracted text.
      -->
      <div
        v-if="message.role === 'user' && message.attachments && message.attachments.length > 0"
        class="attachment-chips"
      >
        <button
          v-for="(attachment, i) in message.attachments"
          :key="i"
          type="button"
          class="attachment-chip"
          :title="`View ${attachment.filename}`"
          @click="openAttachment(attachment)"
        >
          <v-icon size="14" color="grey darken-1">{{ iconForKind(attachment.kind) }}</v-icon>
          <span class="chip-name">{{ attachment.filename }}</span>
          <v-icon size="12" color="grey">mdi-eye-outline</v-icon>
        </button>
      </div>
    </div>

    <!-- Viewer dialog -->
    <AttachmentViewer
      :attachment="activeAttachment"
      @close="activeAttachment = null"
    />
  </div>
</template>

<script>
import { computed, defineComponent, ref, watch } from 'vue';
import AtlasLogo from './AtlasLogo.vue';
import MarkdownContent from './MarkdownContent.vue';
import { cleanStreamingText } from '@rm-ui/agent/agentChat';
import { stripWorkflowActionMarker } from '@rm-ui/agent/uiBlockSpec';
import { hasPageAction, dispatchPageAction, useAtlasFlyout } from '@/composables/agent/atlasFlyout';

const REVIEW_PAGE_ACTION_KIND = 'document.suggest-content';
import ToolCallIndicator from './ToolCallIndicator.vue';
import AttachmentViewer from './AttachmentViewer.vue';
import UiBlockRenderer from './blocks/UiBlockRenderer.vue';

export default defineComponent({
  name: 'ChatMessage',
  components: { AtlasLogo, MarkdownContent, ToolCallIndicator, AttachmentViewer, UiBlockRenderer },
  props: {
    message: { type: Object, required: true },
    /** True only for the latest assistant message — gates the Retry button. */
    canRetry: { type: Boolean, default: false },
    /**
     * Matching entry from the shared `readyToCreate` map — present
     * while this chain's output is Queued. When `kickedOff` flips
     * true the CTA shows "Starting…" and disables. Both this CTA and
     * the RHS panel's Queued strip derive from the same entry, so
     * clicking Run on either surface updates the other.
     */
    readyEntry: { type: Object, default: null },
    /**
     * True when a `docCreationProgress` record exists whose
     * `execution_id` matches this message's chain. Once set, the CTA
     * switches to a static "Running" chip — the RHS panel's live
     * strip takes over as the source of progress updates.
     */
    docCreationInProgress: { type: Boolean, default: false },
    /** The surface's default workflow key — forwarded to workflow blocks
     * so the Exit action bounces straight back to the default flow. */
    exitWorkflowKey: { type: String, default: null },
    /** Instance the host's resume chip currently offers — forwarded so
     * the parked hint on superseded cards agrees with the chip. */
    parkedInstanceId: { type: Number, default: null },
  },
  emits: ['ref-click', 'follow-up', 'retry', 'create-document', 'compare-documents', 'compare-levels', 'open-summary', 'open-document'],
  setup(props, { emit }) {
    // Active flyout page context. Read here (not just at watcher time)
    // so the auto-narrow watcher below can gate on its `kind` —
    // dispatching `document.filter` while the user is in a single-doc
    // context (modal open over alljobs) would re-narrow the
    // still-mounted alljobs grid behind the modal.
    const { pageContext } = useAtlasFlyout();

    // The context this message was BORN under. The auto-narrow below
    // keys off this rather than the live context: closing a document
    // flips the global context to 'documents.grid' while a doc-born
    // message is still on screen — and the live guard would then wrongly
    // let it narrow the grid to the doc the user just closed. A message
    // surfaced while viewing a single document must never auto-narrow
    // the grid, whatever the context later flips to.
    //
    // The birth context is read from `message.contextKind`, stamped on
    // the message by the shared agentChat composable at push/replay
    // time — NOT captured here at mount. A mount-time capture doesn't
    // survive remounts: when a document with an inline Atlas panel
    // closes, that host unmounts and the global flyout re-renders the
    // same (still doc-conversation) messages under the already-restored
    // grid context, re-classifying them as grid-born and narrowing the
    // grid to the doc that was just closed. The mount-time capture
    // remains only as a fallback for messages without a stamp.
    const bornInGridContext =
      props.message.contextKind !== undefined
        ? props.message.contextKind === 'documents.grid'
        : (pageContext.value?.kind ?? null) === 'documents.grid';

    /**
     * Currently-open attachment for the viewer. One at a time per message —
     * keeping state per-message-component means no shared mutable state
     * between bubbles in the scroll list.
     */
    const activeAttachment = ref(null);

    /**
     * "Create Document" CTA is driven entirely by the shared lifecycle
     * state passed in from the parent. Queued → `readyEntry` present,
     * `kickedOff=false`. Starting… → `readyEntry.kickedOff=true`.
     * Running → `docCreationInProgress=true`. Dismissed / completed →
     * both absent, so the CTA hides (doesn't read as actionable).
     */
    const canCreateDocument = computed(
      () =>
        props.message.role === 'assistant' &&
        props.message.hasDocumentOutput === true &&
        !props.message.pendingNarration &&
        props.message.chainExecutionId != null &&
        (props.readyEntry != null || props.docCreationInProgress === true),
    );

    const docCreationSubmitting = computed(() => props.readyEntry?.kickedOff === true);
    const docCreationStarted = computed(() => props.docCreationInProgress === true);

    /**
     * Status phrasing mirrors the Agents-panel card — "Ready to run"
     * until clicked, "Starting…" while the REST kickoff is in flight,
     * "Running" after the panel card takes over.
     */
    const ctaStatusLabel = computed(() => {
      if (docCreationStarted.value) return 'Running';
      if (docCreationSubmitting.value) return 'Starting…';
      return 'Ready to run';
    });

    function onCreateDocument() {
      if (docCreationSubmitting.value || docCreationStarted.value) return;
      const executionId = props.message.chainExecutionId;
      if (executionId == null) return;
      // Parent handles REST kickoff, which flips `readyEntry.kickedOff`
      // in the shared composable — propagates back reactively.
      emit('create-document', executionId);
    }

    /**
     * Documents this message surfaced, keyed by RM reference.
     *
     * Sourced from `message.entityInstances` — the agent service
     * pre-extracts every `{ doc_id, reference, doc_name }` referenced
     * by tool results into an entity-keyed envelope, server-side
     * deduped, so we just lift the `document` entry. A Map keyed by
     * reference is the canonical shape because the primary consumer is
     * MarkdownContent's chip-enrichment (point lookup by reference);
     * the doc-chip row and Compare CTA iterate the values.
     *
     * Refs without a numeric `doc_id` are dropped — downstream
     * consumers (DocumentCompare, document-summary popover) require it.
     */
    const documentIndex = computed(() => {
      const map = new Map();
      const instances = Array.isArray(props.message.entityInstances)
        ? props.message.entityInstances.find((e) => e && e.entity === 'document')?.instances
        : null;
      for (const inst of Array.isArray(instances) ? instances : []) {
        if (!inst || !inst.reference) continue;
        const id = Number(inst.doc_id);
        if (!Number.isFinite(id) || id <= 0) continue;
        map.set(inst.reference, {
          doc_id: id,
          doc_name: inst.doc_name ?? null,
          doc_type: inst.doc_type ?? null,
          doc_status_text: inst.doc_status_text ?? null,
          lf_evaluation_status: inst.lf_evaluation_status ?? null,
          lf_evaluation_flow_type: inst.lf_evaluation_flow_type ?? null,
        });
      }
      // Background narration messages (doc-creation completion) carry a
      // `documents` array instead of entity instances — index those too
      // so "created RM-xxx" chips open the summary rather than falling
      // back to a chat lookup.
      const stamped = Array.isArray(props.message.documents) ? props.message.documents : [];
      for (const doc of stamped) {
        if (!doc || !doc.reference || map.has(doc.reference)) continue;
        const id = Number(doc.doc_id);
        if (!Number.isFinite(id) || id <= 0) continue;
        map.set(doc.reference, {
          doc_id: id,
          doc_name: doc.doc_name ?? null,
          doc_type: doc.doc_type ?? null,
          doc_status_text: doc.doc_status_text ?? doc.state ?? null,
        });
      }
      return map;
    });

    /** Array view of `documentIndex` for v-for / emit / watch consumers. */
    const documents = computed(() =>
      Array.from(documentIndex.value, ([reference, doc]) => ({
        reference,
        doc_id: doc.doc_id,
        doc_name: doc.doc_name,
        doc_type: doc.doc_type,
        doc_status_text: doc.doc_status_text,
        lf_evaluation_status: doc.lf_evaluation_status,
        lf_evaluation_flow_type: doc.lf_evaluation_flow_type,
      })),
    );

    /**
     * Subset of `documents` that carry a levelling evaluation — populated only
     * when this turn surfaced jobs via a levelling-aware search. Their presence
     * is what marks this a "levelling conversation"; the Compare levels CTA
     * uses exactly these (never un-evaluated jobs).
     */
    const evaluatedDocuments = computed(() =>
      documents.value.filter((d) => !!d.lf_evaluation_status),
    );

    /**
     * Host-page registration probes. The flyout forwards these chips
     * as page actions; rendering them on routes where no handler is
     * wired would dead-end the click. Both are reactive `computed`s
     * so navigation between pages with/without registration updates
     * the CTA visibility without remounting the message.
     */
    // A document-summary popover is ALWAYS available in-app: pages that
    // registered `document.summary` own it, and both hosts otherwise
    // fall back to their own DocViewPopUp. (The MCP surface renders
    // blocks in its own iframe bundle — this component is portal-only,
    // so "in-app only" affordances can gate on this constant.)
    const canShowDocumentSummaryHandler = computed(() => true);
    // In-app document open is likewise always available: pages that
    // registered `document.open` own it, and everywhere else the
    // flyout emits `openDocument` to its host (App.vue's standard
    // document dialog / Document_Holder), so workflow cards never
    // fall back to a new-tab deep link inside the portal.
    const canOpenDocumentHandler = computed(() => true);
    const canCompareDocumentsHandler = hasPageAction('document.compare');
    const canCompareLevelsHandler = hasPageAction('document.compare-levels');
    const canHandleReviewAction = hasPageAction(REVIEW_PAGE_ACTION_KIND);

    /**
     * Re-open the suggestion review wizard. True when:
     *  - This message carries a `document.suggest-content` page action
     *    payload (set when the turn that emitted it was finalised), and
     *  - The current host page has a handler registered for that kind.
     * Hiding the button on routes without a handler keeps the chat
     * navigable across pages — the same conversation viewed elsewhere
     * just doesn't surface the affordance.
     */
    const canReopenReview = computed(() => {
      const pa = props.message.pageAction;
      if (!pa || pa.kind !== REVIEW_PAGE_ACTION_KIND) return false;
      const suggestions = pa.payload?.suggestions;
      if (!Array.isArray(suggestions) || suggestions.length === 0) return false;
      return canHandleReviewAction.value;
    });

    function onReopenReview() {
      const pa = props.message.pageAction;
      if (!pa || pa.kind !== REVIEW_PAGE_ACTION_KIND) return;
      dispatchPageAction(REVIEW_PAGE_ACTION_KIND, pa.payload);
    }

    const canShowDocumentSummary = computed(
      () => canShowDocumentSummaryHandler.value && documentIndex.value.size > 0,
    );

    const canCompareDocuments = computed(
      () =>
        canCompareDocumentsHandler.value &&
        !props.message.pendingNarration &&
        // General principle: when the message carries workflow cards,
        // the CARD owns its document interactions (doc lists render
        // their own Compare/Open/summary affordances) — a message-level
        // CTA would double up.
        uiBlocksToRender.value.length === 0 &&
        documentIndex.value.size >= 2 &&
        documentIndex.value.size < 15,
    );

    /**
     * "Compare levels" CTA visibility. Shown only when:
     *  - the host page registered a `document.compare-levels` handler (so the
     *    click lands somewhere — i.e. the jobs grid where levelling lives), and
     *  - this turn surfaced ≥2 evaluated jobs (the levelling-conversation
     *    signal). No upper bound — the CTA stays available however many
     *    evaluated jobs the turn surfaced.
     * The CTA passes only the evaluated documents.
     */
    const canCompareLevels = computed(
      () =>
        canCompareLevelsHandler.value &&
        !props.message.pendingNarration &&
        evaluatedDocuments.value.length >= 2,
    );

    /**
     * Filter Claude's free-text follow-ups down to the chips we actually
     * want to render. When a dedicated action CTA already covers an
     * intent (Compare today; others can register here later), drop the
     * matching chip so the user sees one canonical affordance per intent
     * instead of a chat-style chip *and* a richer CTA side-by-side.
     *
     * Word-stem regex on the chip text is enough because follow-ups are
     * short, intent-laden phrases — incidental mentions of "compare"
     * inside a longer sentence don't show up as chip labels.
     */
    const displayFollowUps = computed(() => {
      const followUps = Array.isArray(props.message.followUps) ? props.message.followUps : [];
      const patterns = [];
      if (canCompareDocuments.value) patterns.push(/\bcompar/i);
      if (patterns.length === 0) return followUps.filter((q) => typeof q === 'string');
      return followUps.filter(
        (q) => typeof q === 'string' && !patterns.some((p) => p.test(q)),
      );
    });

    /**
     * Auto-narrow the host grid to the documents this turn surfaced.
     *
     * Subtlety: finalised assistant messages are pushed into the
     * messages array with `entityInstances` ALREADY populated — there
     * is no empty → populated transition on the same object, so a
     * plain `watch(documents)` without `immediate` never fires.
     * We use `immediate: true` and gate on `isLatestAssistant` so:
     *   - The newly-finalised turn dispatches (it's the latest).
     *   - When a NEWER turn arrives this message's flag flips false
     *     and we bail; the new message's watcher fires instead.
     *   - On conversation reload only the last assistant message
     *     fires (everything else has the flag cleared), so the grid
     *     filters to "what Atlas last surfaced" without N spurious
     *     dispatches racing on mount.
     *
     * Payload uses `docIds` — the database PK is more reliable than
     * `reference` (system_number) for unambiguous row targeting, and
     * the host consumer filters via `docIdsIncluded` rather than a
     * column-level field filter.
     */
    watch(
      // The reactive source is the message's OWN data only — never the
      // page context. A context flip must not re-fire the narrow: closing
      // a document over the grid flips the global context to
      // 'documents.grid' synchronously while this (document-conversation)
      // message is still mounted, before the chat swaps to the grid
      // conversation on nextTick. Gating on the live context would let
      // that flip drive a dispatch; we gate on `bornInGridContext`
      // (the message-stamped birth context) instead, so a doc-born
      // message stays inert — even across the inline-host → global-flyout
      // remount that closing a document causes.
      () => ({
        items: documents.value,
        isLatest: props.message.isLatestAssistant === true,
        pending: props.message.pendingNarration === true,
      }),
      ({ items, isLatest, pending }) => {
        if (!isLatest || pending || items.length === 0) return;
        // Auto-narrow is a grid-view affordance — the only context where
        // "the agent surfaced these docs, point the rows at them" is the
        // user's intent. A message born under a single-document context
        // (modal open over alljobs/myjobs, alljobs handler still
        // registered underneath) must never narrow the hidden grid —
        // that surfaces as a one-row grid the moment the modal closes.
        if (!bornInGridContext) return;
        const docIds = items.map((d) => d.doc_id).filter((id) => id != null);
        if (docIds.length === 0) return;
        dispatchPageAction('document.filter', { docIds });
      },
      { immediate: true },
    );

    /**
     * Workflow blocks stamped on this message (engine-authored, already
     * shape-guarded server-side; UiBlockRenderer re-validates before
     * mounting).
     */
    const uiBlocksToRender = computed(() =>
      Array.isArray(props.message.uiBlocks) ? props.message.uiBlocks : [],
    );

    /**
     * Within one message, only the LAST block per workflow instance is
     * the instance's current step — a turn that advanced a workflow more
     * than once (e.g. exit-and-restart then a choice) stamps every card
     * it produced, and rendering the earlier ones live reads as two
     * in-progress workflows (user report, 2026-09-03). The API now drops
     * superseded blocks at persist time; this guard covers history
     * persisted before that fix. Blocks without an instance id are
     * left interactive.
     */
    function isBlockCurrent(block, blockIndex) {
      const instanceId = block?.workflow?.instance_id;
      if (typeof instanceId !== 'number') return true;
      const blocks = uiBlocksToRender.value;
      for (let i = blocks.length - 1; i > blockIndex; i -= 1) {
        if (blocks[i]?.workflow?.instance_id === instanceId) return false;
      }
      return true;
    }

    /**
     * Acting on a block composes a `[workflow:action]` message; forward
     * it up the follow-up channel so both hosts (flyout, full page)
     * route it into sendMessage with zero new wiring.
     */
    function onUiBlockAction(messageText) {
      emit('follow-up', messageText);
    }

    /**
     * User bubbles hide the machine-facing `[workflow:action]` marker
     * line — the user sees only the friendly "I chose: …" part, while
     * history keeps the full text the model parses.
     */
    const displayUserText = computed(() =>
      stripWorkflowActionMarker(props.message.text),
    );

    function openAttachment(attachment) {
      activeAttachment.value = attachment;
    }

    function iconForKind(kind) {
      if (kind === 'pdf') return 'mdi-file-pdf-box';
      if (kind === 'docx') return 'mdi-file-word-box';
      return 'mdi-file-document-outline';
    }

    /**
     * Transient "Copied!" flash on the copy button. Reset after 1.5s so
     * the user sees confirmation but it doesn't stick.
     */
    const copied = ref(false);

    /**
     * Ref-chip clicks in markdown body. The chip itself carries the
     * full `{ reference, doc_id, doc_name }` payload (enriched via
     * `documentIndex` at render time), so no lookup is needed here.
     *
     * If the chip resolved to a real document and we have the click event,
     * emit `open-summary` and let the hosting chat surface present the
     * DocSummary popover — the full-page chat and the flyout both handle
     * this (the flyout prefers a host page's handler, falling back to its
     * own popover), so the behaviour is uniform wherever a ref appears.
     * Only when the chip has no resolvable doc_id do we fall through to the
     * chat-side "Tell me about X" prompt.
     */
    function onRefClick(refData, event) {
      if (refData && refData.doc_id != null && event) {
        emit('open-summary', event, refData);
        return;
      }
      // Forward the click event too — hosts resolve bare references to a
      // doc_id and open the summary anchored at the chip, falling back
      // to a chat lookup only when resolution fails.
      emit('ref-click', refData, event);
    }

    async function copyToClipboard() {
      try {
        // Raw markdown source, not rendered HTML — makes the paste useful
        // in Slack/Notion/code editors.
        await navigator.clipboard.writeText(props.message.text);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 1500);
      } catch {
        // Clipboard API can fail in insecure contexts or when blocked by
        // permissions. Silent for POC.
      }
    }

    return {
      activeAttachment,
      openAttachment,
      iconForKind,
      copied,
      copyToClipboard,
      cleanStreamingText,
      canCreateDocument,
      docCreationSubmitting,
      docCreationStarted,
      ctaStatusLabel,
      onCreateDocument,
      documents,
      documentIndex,
      evaluatedDocuments,
      canShowDocumentSummary,
      canShowDocumentSummaryHandler,
      canOpenDocumentHandler,
      canCompareDocuments,
      canCompareDocumentsHandler,
      canCompareLevels,
      displayFollowUps,
      onRefClick,
      canReopenReview,
      onReopenReview,
      uiBlocksToRender,
      isBlockCurrent,
      onUiBlockAction,
      displayUserText,
    };
  },
});
</script>

<style scoped>
.chat-message {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.chat-message--user {
  justify-content: flex-end;
}
/* Assistant turns stack: mark row on top, indented body underneath. */
.chat-message--assistant {
  display: block;
}
.atlas-turn-mark {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.atlas-turn-mark__name {
  font-size: 12px;
  font-weight: 600;
  color: rgb(75 85 99);
  letter-spacing: -0.01em;
}
.bubble-wrapper {
  max-width: 42rem;
  min-width: 0;
}
/* Body indent lines the turn's content up under the mark's label. */
.chat-message--assistant .bubble-wrapper {
  max-width: none;
  padding-left: 26px;
}
.chat-message--user .bubble-wrapper {
  max-width: 80%;
}
.tool-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.bubble-hover-wrap {
  position: relative;
}
/* Reasoning trail — intermediate monologue shown above the answer. */
.thinking-trail {
  margin-bottom: 8px;
  border-radius: 12px;
  border: 1px solid rgb(229 231 235);
  background: rgba(249, 250, 251, 0.6);
  padding: 8px 12px;
}
.thinking-trail__summary {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  color: rgb(107 114 128);
  cursor: pointer;
  user-select: none;
  list-style: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.thinking-trail__summary::-webkit-details-marker {
  display: none;
}
.thinking-trail__summary::before {
  content: '▸';
  font-size: 10px;
  color: rgb(156 163 175);
  transition: transform 0.15s;
}
.thinking-trail[open] > .thinking-trail__summary::before {
  transform: rotate(90deg);
}
.thinking-trail__body {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: rgb(75 85 99);
}
/* Pending-narration placeholder — spinning chip shown while the backend
   synthesises the chain's narrated output. Replaced in place once the
   real narration arrives via `agent:background_message`. */
.pending-narration {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  background: rgb(243 244 246);
  color: rgb(75 85 99);
  font-size: 14px;
}
.pending-narration-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(99, 102, 241, 0.25);
  border-top-color: rgb(99 102 241);
  animation: pending-narration-spin 0.9s linear infinite;
}
@keyframes pending-narration-spin {
  to { transform: rotate(360deg); }
}
/* Blinking caret for partial streamed narration — signals "more coming". */
.streaming-cursor {
  display: inline-block;
  margin-left: 2px;
  color: rgb(99 102 241);
  animation: streaming-cursor-blink 0.9s steps(2, end) infinite;
}
@keyframes streaming-cursor-blink {
  50% { opacity: 0; }
}
.bubble {
  padding: 10px 12px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.6;
}
.msg-actions {
  position: absolute;
  right: 8px;
  bottom: -8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.12s ease;
}
.bubble-hover-wrap:hover .msg-actions,
.msg-actions:focus-within {
  opacity: 1;
}
.msg-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  font-size: 10px;
  color: rgb(107 114 128);
  background: white;
  border: 1px solid rgb(229 231 235);
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: color 0.12s ease;
  font-family: inherit;
}
.msg-action-btn:hover {
  color: rgb(17 24 39);
}
/* Quiet user bubble (DS AtlasChat): sunken grey with dark text, so the
   indigo stays reserved for Atlas identity + actions. */
.bubble--user {
  background: rgb(249 250 251);
  color: rgb(17 24 39);
  border-bottom-right-radius: 6px;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
}
/* Assistant replies render as plain prose under the mark — no bubble. */
.bubble--assistant {
  background: transparent;
  padding: 0;
  border-radius: 0;
  color: rgb(75 85 99);
  font-size: 15px;
  line-height: 1.65;
}
.user-text { white-space: pre-wrap; }
.no-response {
  color: rgb(156 163 175);
  font-style: italic;
}

/*
 * Document Creation Agent CTA — styled as a full agent card that
 * mirrors the right-hand Agents panel's card, so clicking Run feels
 * like handing off to the same agent the user sees offering the
 * action inline. Shape, palette and motion are intentionally
 * identical to .agent-card / .agent-card--doc in AgentsPanel.vue.
 */
.doc-cta-card {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(110, 231, 183, 0.7);
  background: linear-gradient(to bottom, rgba(209, 250, 229, 0.6) 0%, rgba(255, 255, 255, 0.4) 100%);
  box-shadow: 0 1px 2px rgba(16, 185, 129, 0.08);
}
.doc-cta-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.doc-cta-card__avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  box-shadow: 0 0 0 1px rgb(110 231 183);
}
.doc-cta-card__avatar--pulsing {
  animation: doc-cta-avatar-pulse 2.4s ease-in-out infinite;
}
@keyframes doc-cta-avatar-pulse {
  0%, 100% {
    box-shadow: 0 0 0 1px rgb(110 231 183), 0 0 0 0 rgba(16, 185, 129, 0.35);
  }
  50% {
    box-shadow: 0 0 0 1px rgb(110 231 183), 0 0 0 6px rgba(16, 185, 129, 0);
  }
}
.doc-cta-card__meta {
  min-width: 0;
  flex: 1;
}
.doc-cta-card__name {
  font-size: 13px;
  font-weight: 600;
  color: rgb(17 24 39);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.doc-cta-card__status-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.doc-cta-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(16 185 129);
  flex-shrink: 0;
}
.doc-cta-card__dot--pulse {
  animation: doc-cta-dot-pulse 1.4s ease-in-out infinite;
}
@keyframes doc-cta-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.6; transform: scale(0.85); }
}
.doc-cta-card__status {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(4 120 87);
}
.doc-cta-card__btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  background: rgb(5 150 105);
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.doc-cta-card__btn:hover { background: rgb(4 120 87); }
.doc-cta-card__btn:disabled { opacity: 0.6; cursor: not-allowed; }
.doc-cta-card__started {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: rgb(6 95 70);
  background: rgb(209 250 229);
}
.doc-cta-card__spin {
  animation: doc-cta-spin 1s linear infinite;
}
@keyframes doc-cta-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.doc-cta-card__subtitle {
  margin: 8px 0 0 0;
  font-size: 11px;
  color: rgb(75 85 99);
  line-height: 1.45;
}

/* Workflow ui_block row — one card per block, stacked. */
.ui-blocks {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Follow-up chip row (assistant suggestions) */
.follow-up-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.follow-up-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  border: 1px solid rgb(209 213 219);
  background: white;
  font-size: 12px;
  color: rgb(55 65 81);
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease;
  font-family: inherit;
}
.follow-up-chip:hover {
  border-color: rgb(129 140 248);
  background: rgb(238 242 255);
  color: rgb(67 56 202);
}
.document-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 2px;
  border: 1px solid rgb(76, 141, 238);
  background: white;
  font-size: 12px;
  color: rgb(55 65 81);
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease;
  font-family: inherit;
}
.document-chip:hover {
  border-color: rgb(221, 221, 221);
  background: rgb(238 242 255);
  color: rgb(67 56 202);
}

/*
 * Compare-documents CTA. Sits between the follow-up chips and the
 * attachment row when the agent surfaces 2+ documents the user can
 * inspect side-by-side. Indigo fill so it reads as "the next action"
 * the same way the follow-up chips do, but with stronger visual weight
 * because it opens a full-screen modal rather than firing another turn.
 */
.compare-cta {
  /* margin-top: 10px; */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 0;
  border-radius: 9999px;
  background: rgb(79 70 229);
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(79, 70, 229, 0.25);
  transition: background 0.15s ease, transform 0.12s ease;
  font-family: inherit;
}
.compare-cta:hover {
  background: rgb(67 56 202);
  transform: translateY(-1px);
}

/*
 * Re-open review panel CTA. Same indigo pill family as the compare CTA
 * so both read as "the next dedicated action on this turn", but slightly
 * softer (no shadow) since re-opening is recoverable — not a commit.
 */
.review-reopen-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 0;
  border-radius: 9999px;
  background: rgb(79 70 229);
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.12s ease;
  font-family: inherit;
}
.review-reopen-cta:hover {
  background: rgb(67 56 202);
  transform: translateY(-1px);
}

/* Attachment chip row */
.attachment-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  justify-content: flex-end;
}
.attachment-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid rgb(209 213 219);
  background: white;
  font-size: 12px;
  color: rgb(55 65 81);
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
  font-family: inherit;
  max-width: 100%;
}
.attachment-chip:hover {
  border-color: rgb(129 140 248);
  background: rgb(249 250 251);
}
.chip-name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 14rem;
}
</style>
